import type { DroppableProvided, DraggableProvided, DropResult, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import type { WidgetItem, WidgetItemId } from '../../../domain/company_widgets';
import CompanyWidgetsService from '../../../services/companyWidgets';
import { translate } from '../../../services/i18n';

import { StyledWidgetPickerList } from './styles';
import { useWidgetPickerTracking } from './useWidgetPickerTracking';
import { WidgetPickerItem } from './WidgetPickerItem';

interface WidgetPickerProps {
  widgets: WidgetItem[];
  onSelectWidgets: (widgets: WidgetItem[]) => void;
}

export const WidgetPickerContent: React.FC<WidgetPickerProps> = ({ widgets, onSelectWidgets }) => {
  const { getWidgetName } = CompanyWidgetsService;

  const { trackClickOnCheckbox } = useWidgetPickerTracking();

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const reorderedWidgets = Array.from(widgets);
    const [movedWidget] = reorderedWidgets.splice(result.source.index, 1);
    reorderedWidgets.splice(result.destination.index, 0, movedWidget);

    onSelectWidgets(reorderedWidgets);
  };

  const handleClickOnCheckbox = (widgetId: WidgetItemId) => {
    let isWidgetChecked = false;

    const updatedWidgets = widgets.map((widget) => {
      if (widget.id === widgetId) {
        isWidgetChecked = !widget.isChecked;
        return { ...widget, isChecked: isWidgetChecked };
      }
      return widget;
    });
    trackClickOnCheckbox({ id: widgetId, isChecked: isWidgetChecked }, updatedWidgets);
    onSelectWidgets(updatedWidgets);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="widgets">
        {(provided: DroppableProvided) => (
          <StyledWidgetPickerList {...provided.droppableProps} ref={provided.innerRef}>
            <li key="companyWidget">
              <WidgetPickerItem isSelected isDisabled label={translate('company.overview')} />
            </li>

            {widgets.map(({ id: widgetId, isChecked }, index) => {
              return (
                <Draggable key={widgetId} draggableId={widgetId} index={index}>
                  {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        key={widgetId}
                      >
                        <WidgetPickerItem
                          isSelected={isChecked}
                          isDragging={snapshot.isDragging}
                          isDisabled={false}
                          label={translate(getWidgetName(widgetId))}
                          onClick={() => handleClickOnCheckbox(widgetId)}
                        />
                      </div>
                    );
                  }}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </StyledWidgetPickerList>
        )}
      </Droppable>
    </DragDropContext>
  );
};
